import { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import { useWell } from "../WellContext";
import {
  Line, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart, Cell,
  PieChart, Pie, Label, BarChart, AreaChart, LineChart,
} from "recharts";
import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";
const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",lime:"#b0ff4a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const FIELD_META = {
  ArrivalVelocity: {
    label: "Plunger Arrival Velocity", unit: "ft/min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 350)   return { color: C.red,   text: `TOO SLOW (${v.toFixed(0)} ft/min), below safe min 350. Extend shut-in.` };
      if (v > 1200)  return { color: C.amber, text: `TOO FAST (${v.toFixed(0)} ft/min), above 1200. Reduce casing pressure target.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(0)} ft/min), optimal plunger travel range.` };
    },
    advice: v => v < 350 ? "▽ Too slow (<350 ft/min), plunger may drop back. Extend shut-in time or reduce line pressure." : v > 1200 ? "▶ Too fast (>1200 ft/min), excess energy. Reduce shut-in time to save gas." : "◆ Optimal range (350–1200 ft/min). Perfect lift!",
  },
  FlowRateAtClose: {
    label: "Gas Production Rate (at valve close)", unit: "MCF/day",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 50)   return { color: C.red,   text: `LOW (${v.toFixed(1)} MCF/day), well under-producing. Check liquid loading.` };
      return          { color: C.teal,  text: `${v.toFixed(1)} MCF/day, gas sold at valve close. Higher = more revenue.` };
    },
    advice: v => v < 50 ? "▲ Low flow rate, well may be under-performing. Check valve, plunger, separator." : v > 500 ? "◆ Excellent production rate!" : "◆ Normal flow rate.",
  },
  CsgAtOpen: {
    label: "Casing Pressure at Valve Open", unit: "psi",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 300)   return { color: C.red,   text: `LOW (${v.toFixed(0)} psi), not enough reservoir drive to push plunger up.` };
      return           { color: C.cyan,  text: `${v.toFixed(0)} psi, reservoir pressure driving plunger upward.` };
    },
    advice: v => v < 300 ? "▲ Low, may not have enough gas to lift plunger. Increase shut-in time." : v > 800 ? "◆ High, good reservoir pressure. Consider opening valve sooner." : "◆ Normal range. Monitor trends.",
  },
  LoadFactor: {
    label: "Load Factor", unit: "",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v >= 0.5)  return { color: C.red,   text: `OVERLOADED (${v.toFixed(3)}), plunger WILL NOT arrive. Extend shut-in NOW.` };
      if (v >= 0.35) return { color: C.amber, text: `APPROACHING LIMIT (${v.toFixed(3)}), close to 0.5 threshold, monitor.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(3)}), plunger can lift this liquid column.` };
    },
    advice: v => v >= 0.5 ? "■ Load factor ≥0.5, plunger cannot lift liquid. Extend shut-in significantly before opening." : v >= 0.35 ? "▲ Load factor approaching 0.5, add 10–15 min to shut-in time as precaution." : "◆ Load factor safe. Plunger can lift current liquid column.",
  },
  TimeOffTotal: {
    label: "Shut-In Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min shut-in, valve closed, casing pressure rebuilding.` };
    },
    advice: v => v < 30 ? "▲ Short shut-in, casing may not build enough pressure. Increase to 45–90 min." : v > 300 ? "▲ Very long shut-in, wasting production time. Reduce if casing pressure is sufficient." : "◆ Shut-in time is in good range.",
  },
  TimeAfterFlow: {
    label: "Afterflow Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min afterflow, this is your gas production window.` };
    },
    advice: v => v < 10 ? "▲ Short after-flow, closing valve too early. Extend to capture more gas." : v > 120 ? "▲ Very long after-flow, may delay next cycle." : "◆ Good after-flow window.",
  },
  score: {
    label: "Cycle Health Score", unit: "/100",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v >= 75)  return { color: C.green, text: `EXCELLENT (${v}), all plunger physics operating within spec.` };
      if (v >= 55)  return { color: C.green, text: `GOOD (${v}), minor deviations, within acceptable range.` };
      if (v >= 35)  return { color: C.amber, text: `WARNING (${v}), multiple parameters out of spec. Review cycle.` };
      return          { color: C.red,   text: `CRITICAL (${v}), serious issues. Check velocity, load factor, arrival.` };
    },
    advice: v => v >= 75 ? "◆ Excellent cycle score, well operating within all physics limits." : v >= 55 ? "◆ Good score, minor deviations. Monitor trend for deterioration." : v >= 35 ? "▲ Warning, multiple parameters out of spec. Review velocity and load factor." : "■ Critical score, serious issues detected. Check plunger arrival and casing pressure.",
  },
  anomaly_score: {
    label: "Anomaly Score", unit: "/10",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v < 3.33) return { color: C.green, text: `GOOD (${v.toFixed(1)}), cycle within expected operating range.` };
      if (v < 6.66) return { color: C.amber, text: `WARNING (${v.toFixed(1)}), some parameters deviated from normal.` };
      return          { color: C.red,   text: `CRITICAL (${v.toFixed(1)}), unusual cycle, investigate triggers below.` };
    },
    advice: v => v < 3.33 ? "◆ Good, all parameters within expected statistical range. No action needed." : v < 6.66 ? "▲ Warning, one or more parameters deviated. Check triggered features and compare to prior cycles." : "■ Critical, very unusual cycle. Investigate triggered parameters immediately.",
  },
  // FIXED
  violations: {
    label: "Physics Violations", unit: "count",
    interpret: v => {
      if (v == null || v === 0) return { color: C.green, text: "0 violations, cycle passed all physics equations." };
      if (v === 1)              return { color: C.amber, text: `WARNING: 1 violation, one physics equation failed this cycle.` };
      return                             { color: C.red,   text: `CRITICAL: ${v} violations, multiple physics failures. Cycle is out of spec.` };
    },
    advice: v => (!v || v === 0) ? "◆ Zero violations, well operating within all physics limits this cycle." : v === 1 ? "▲ Warning, one physics violation. Check load factor and casing pressure at open." : `■ Critical, ${v} violations. Multiple physics failures. Review load factor, velocity, and shut-in timing.`,
  },
  actual: {
    label: "Actual Production", unit: "MCF/day",
    interpret: v => v != null ? { color: C.teal, text: `Actual measured: ${v.toFixed(2)} MCF/day` } : { color: C.muted, text: "No data" },
    advice: v => v != null ? (v < 50 ? "▲ Low production rate, check for liquid loading or valve issues." : "◆ Normal production rate for this cycle.") : null,
  },
  forecast: {
    label: "AI Model Forecast", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Model predicts ${v.toFixed(2)} MCF/day for this future cycle.` } : { color: C.muted, text: "No forecast" },
    advice: v => v != null ? `Model forecasts ${v.toFixed(1)} MCF/day. Compare to your historical average to assess trajectory.` : null,
  },
  upper_80: {
    label: "Upper 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Optimistic bound: ${v.toFixed(2)} MCF/day (top 10% of outcomes).` } : { color: C.muted, text: "N/A" },
    advice: () => "Upper bound of the 80% confidence interval, actual production will exceed this only 10% of the time.",
  },
  lower_80: {
    label: "Lower 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.blue, text: `Conservative bound: ${v.toFixed(2)} MCF/day (bottom 10% of outcomes).` } : { color: C.muted, text: "N/A" },
    advice: () => "Lower bound of the 80% confidence interval, actual production falls below this only 10% of the time.",
  },
  rolling_avg: {
    label: "Rolling Average Score", unit: "/100",
    interpret: v => v != null ? { color: C.cyan, text: `Smoothed trend: ${v.toFixed(1)}/100 over recent cycles.` } : { color: C.muted, text: "N/A" },
    advice: v => v != null ? (v < 55 ? "▲ Rolling average below 55, sustained performance issues. Review shut-in setpoints." : "◆ Rolling average healthy, no sustained degradation trend.") : null,
  },
  significance: {
    label: "Change Significance (σ)", unit: "σ",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "N/A" };
      if (v > 1.5)  return { color: C.red,   text: `CRITICAL High shift (${v.toFixed(2)}σ). This parameter changed significantly vs prior period.` };
      if (v > 0.8)  return { color: C.amber, text: `WARNING Moderate shift (${v.toFixed(2)}σ). Noticeable change, monitor closely.` };
      return          { color: C.green, text: `GOOD Low shift (${v.toFixed(2)}σ). Minor variation, within normal range.` };
    },
    advice: v => v > 1.5 ? "■ Critical, this parameter shifted substantially. Investigate whether it was a setpoint change or a well condition change." : v > 0.8 ? "▲ Warning, moderate shift detected. Monitor over next 5–10 cycles to confirm trend." : "◆ Good, within normal cycle-to-cycle variation. No action needed.",
  },
  arr: {
    label: "Arrival Time", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No arrival data" };
      if (v > 45) return { color: C.red,   text: `CRITICAL (${Number(v).toFixed(1)} min), very slow arrival.` };
      if (v > 25) return { color: C.amber, text: `WARNING (${Number(v).toFixed(1)} min), slower than preferred.` };
      return        { color: C.green, text: `GOOD (${Number(v).toFixed(1)} min), arrival in expected window.` };
    },
    advice: v => v == null ? null : v > 45 ? "■ Critical slow arrival, check liquid load and shut-in energy." : v > 25 ? "▲ Warning, arrival trending slow. Review build-up." : "◆ Good arrival time.",
  },
  csg: {
    label: "Casing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No CP data" } : { color: C.cyan, text: `Casing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Review casing pressure vs recent baseline for step changes or drift.",
  },
  tbg: {
    label: "Tubing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No TP data" } : { color: C.cyan, text: `Tubing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Compare tubing vs line pressure when diagnosing surface restriction.",
  },
  line: {
    label: "Line Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No line data" } : { color: C.muted, text: `Line pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Line pressure is the surface reference for restriction and leakage checks.",
  },
  hit: {
    label: "Flagged Event", unit: "",
    interpret: v => v == null ? { color: C.green, text: "GOOD, no anomaly flag on this cycle." } : { color: C.red, text: `CRITICAL, anomaly marker on this cycle.` },
    advice: v => v == null ? "◆ Good, cycle not flagged." : "■ Critical flagged cycle, review evidence and recommended action.",
  },
  count: {
    label: "Cycle Count", unit: "cycles",
    interpret: v => v != null ? { color: C.muted, text: `${v} cycles in this operating range.` } : { color: C.muted, text: "N/A" },
    advice: v => v > 50 ? "◆ High frequency, this operating range is most common for well." : v < 5 ? "▲ Rare operating range, outlier conditions. Investigate what drives these cycles." : "◆ Normal distribution bin.",
  },
};

const GENERIC_KEYS = new Set([
  "count","value","bin","actual","lstm","target","step",
  "violations","score","anomaly_score","rolling_avg",
  "significance","forecast","upper_80","lower_80",
  "arr","csg","tbg","line","hit","diff","i",
]);

function SmartTooltip({ active, payload, label, note }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const raw      = active && payload?.length ? payload[0]?.payload || {} : {};
  const cycleNum = raw.CycleSerialNumber ?? raw.cycle ?? raw.index ?? null;
  const timeStr  = raw.time || raw.timestamp || raw.date || null;
  const fieldKey = raw._actualFieldKey || null;

  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";

  const allFields = [];

  if (active && payload?.length) {
    for (const p of payload) {
      const rawKey    = p.dataKey || p.name || "";
      // Prefer explicit chart field key when series name is generic
      let lookupKey = rawKey;
      if ((GENERIC_KEYS.has(rawKey) || !FIELD_META[rawKey]) && fieldKey) {
        lookupKey = fieldKey;
      }
      const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey] || null;
      const val       = typeof p.value === "number" ? p.value : (p.value != null && !isNaN(Number(p.value)) ? Number(p.value) : null);

      if (val !== null && rawKey !== "i") {
        const pointStatus = String(raw.status || "").toLowerCase();
        let interp = meta?.interpret?.(val);
        if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: "CRITICAL, flagged anomaly on this cycle." };
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: "WARNING, flagged anomaly on this cycle." };
        } else if (pointStatus === "good" && (raw.hit != null || rawKey === "hitGood")) {
          interp = { color: C.green, text: "GOOD, info-level marker on this cycle." };
        }
        allFields.push({
          field: lookupKey || rawKey,
          label: meta?.label || rawKey,
          value: Math.round(val * 100) / 100,
          unit:  meta?.unit || "",
          status: interp?.text || "",
        });
        // Always set a primary field so AI advice is requested (even without FIELD_META)
        if (primaryField === null) {
          primaryField = lookupKey || rawKey;
          primaryValue = val;
          primaryUnit  = meta?.unit || "";
        }
      }
    }
  }

  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });
  if (cycleNum != null) context.cycle_num = cycleNum;

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const rounded  = typeof primaryValue === "number"
      ? Math.round(primaryValue * 10) / 10 : primaryValue;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, primaryValue, primaryUnit, cycleNum, context, allFields);
  }, [active, primaryField, primaryValue, JSON.stringify(allFields)]);

  useEffect(() => {
    if (!active) { clearAdvice(); }
  }, [active]);

  if (!active || !payload?.length) return null;
  const TIP_W = 360;
  const hasFactors = Array.isArray(raw._factors) && raw._factors.length > 0;
  const TIP_H = hasFactors ? 320 + 26 + raw._factors.length * 16 : 320;
  const GAP   = 14;
  const vw    = window.innerWidth;
  const vh    = window.innerHeight;
  let left    = mousePos.x + GAP;
  let top     = mousePos.y + GAP;
  if (left + TIP_W > vw - 8) left = mousePos.x - TIP_W - GAP;
  if (top  + TIP_H > vh - 8) top  = mousePos.y - TIP_H - GAP;

  const cycleLabel = label != null
    ? (isNaN(Number(String(label))) ? String(label) : `Cycle ${label}`)
    : (cycleNum != null ? `Cycle ${cycleNum}` : null);

  const tooltipContent = (
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${C.border2}`, borderRadius: 12,
      padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 12px 40px #000e", maxWidth: TIP_W,
      zIndex: 99999, pointerEvents: "none",
    }}>
      {/* Header */}
      {(cycleLabel || timeStr || note) && (
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
          {cycleLabel && <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>{cycleLabel}</div>}
          {timeStr    && <div style={{ color: C.muted, fontSize: 9 }}>{timeStr}</div>}
          {note       && <div style={{ color: C.muted, fontSize: 9, marginTop: 2 }}>{note}</div>}
        </div>
      )}

      {/* Series rows */}
      {payload.map((p, i) => {
        const rawKey    = p.dataKey || p.name || "";
        const isGeneric = GENERIC_KEYS.has(rawKey);
        const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
        const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];

        let displayName;
        if      (rawKey === "actual")        displayName = meta?.label ? `Actual, ${meta.label}` : "Actual";
        else if (rawKey === "lstm")          displayName = "AI Model";
        else if (rawKey === "target")        displayName = "Target";
        else if (rawKey === "rolling_avg")   displayName = "Rolling Average";
        else if (rawKey === "forecast")      displayName = "AI Model Forecast";
        else if (rawKey === "upper_80")      displayName = "Upper 80% CI";
        else if (rawKey === "lower_80")      displayName = "Lower 80% CI";
        else if (rawKey === "violations")    displayName = "Physics Violations";
        else if (rawKey === "anomaly_score") displayName = "Anomaly Score";
        else if (rawKey === "significance")  displayName = "Significance (σ)";
        else if (rawKey === "score")         displayName = meta?.label || "Health Score";
        else if (rawKey === "count")         displayName = meta?.label || "Cycle Count";
        else                                 displayName = meta?.label || p.name || rawKey;
        let tooltipColor = p.color || C.cyan;
        if (rawKey === "lower_80") tooltipColor = C.blue;

        const isCountBar = rawKey === "count";
        const unit       = isCountBar ? "cycles" : (meta?.unit || "");
        const val        = p.value;
        const formatted  = typeof val === "number"
          ? (Math.abs(val) >= 100 ? val.toFixed(1) : val.toFixed(2))
          : String(val ?? "N/A");
        // Prefer plot severity from the data point when present so red markers
        // always show CRITICAL (not absolute threshold GOOD/WARNING).
        const pointStatus = String(raw.status || "").toLowerCase();
        const isFlagHit = ["hit", "hitCritical", "hitWarning", "hitGood"].includes(rawKey)
          || raw.hit != null || raw.hitCritical != null || raw.hitWarning != null || raw.hitGood != null;
        let interp = meta?.interpret ? meta.interpret(val) : null;
        const typeLabel = (raw.anomaly_type || "anomaly").replace(/_/g, " ");
        const valTxt = typeof val === "number" ? (Math.abs(val)>=100?val.toFixed(1):val.toFixed(2)) : val;
        if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: `CRITICAL, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.red;
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: `WARNING, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.amber;
        } else if ((pointStatus === "good" || rawKey === "hitGood") && isFlagHit) {
          interp = { color: C.green, text: `GOOD, minor / info-level ${typeLabel} marker (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.green;
        } else if (interp?.color) {
          tooltipColor = interp.color;
        }

        return (
          <div key={i} style={{ marginBottom: i < payload.length - 1 ? 10 : 0 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12,
              paddingBottom: interp ? 4 : 0,
            }}>
              <span style={{ color: C.muted, fontSize: 10, flex: 1, lineHeight: 1.3 }}>
                {displayName}
              </span>
              <span style={{ textAlign: "right", flexShrink: 0 }}>
                <span style={{ color: tooltipColor, fontWeight: 700, fontSize: 12 }}> 
                  {formatted}
                </span>
                {unit && (
                  <span style={{ color: C.muted, fontSize: 9, marginLeft: 3 }}>{unit}</span>
                )}
              </span>
            </div>
            {interp && (
              <div style={{
                marginTop: 3, padding: "4px 8px",
                background: `${interp.color}12`,
                border: `1px solid ${interp.color}33`,
                borderRadius: 6, fontSize: 10,
                color: interp.color, lineHeight: 1.5,
              }}>
                {interp.text}
              </div>
            )}
            {i < payload.length - 1 && (
              <div style={{ height: 1, background: C.border, marginTop: 8 }} />
            )}
          </div>
        );
      })}

      {/* Factors causing the violation (Violations Per Cycle plot only) */}
      {hasFactors && (() => {
        const vCount = Math.round(Number(raw.violations ?? raw.violation_count ?? 0));
        const factorColor = vCount === 1 ? C.amber : C.red;   // warning bar = amber factors; critical = red
        return (
        <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          <div style={{ color: factorColor, fontSize: 10, fontWeight: 700, marginBottom: 5 }}>
            Factors causing this violation
          </div>
          {raw._factors.map((f, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 10, lineHeight: 1.6 }}>
              <span style={{ color: f.role === "violated" ? factorColor : C.muted }}>
                {f.label}
              </span>
              <span style={{ color: f.role === "violated" ? factorColor : C.text, fontWeight: 700, textAlign: "right" }}>
                {f.value}{f.unit ? ` ${f.unit}` : ""}
                {f.limit ? <span style={{ color: C.muted, fontWeight: 400 }}>{` (limit ${f.limit})`}</span> : null}
              </span>
            </div>
          ))}
        </div>
        );
      })()}

      {/* AI Recommendation */}
      <div style={{
        marginTop: 10, borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color: C.muted }}>Generating AI Recommendations…</span>
          : advice
            ? renderAdvice(advice)
            : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>
  );
  return ReactDOM.createPortal(tooltipContent, document.body);
}
const TTip = SmartTooltip;

function makeCycleFormatter() {
  return (val) => { const n=Number(val); return isNaN(n)?String(val):`C${n}`; };
}
function cycleInterval(len,maxLabels=10) {
  if(!len) return 0;
  return Math.max(0,Math.floor(len/maxLabels)-1);
}

/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
const toQuery = ms => `${toInput(ms)} ${fmtHM(ms)}`;

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* rows must carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);

  const rowsFor = (w) => {
    if (w.mode === "all" || tMax == null) return rows;
    if (w.mode === "cyc30") {
      if (rows.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
        const by = {};
        rows.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
        return Object.values(by).flatMap(a => a.slice(-30));
      }
      return rows.slice(-30);
    }
    if (w.mode === "custom") {
      const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
      const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
      return rows.filter(d => d._t != null && d._t >= f && d._t <= t);
    }
    const p = PRESETS.find(x => x.id === w.mode);
    return rows.filter(d => d._t != null && d._t >= tMax - p.ms);
  };
  const view = useMemo(() => rowsFor(win), [rows, win, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, rowsFor({ mode: p.id }).length])), [rows, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all" };
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `index` and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, nTicks = 5, labelText) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i].index);
  const lookup = new Map(stamped.map(r => [r.index, r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey="index" ticks={ticks} interval={0} tickLine={false} axisLine={false} height={32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>

      
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}

/* window-aware versions of the backend aggregates (only used when the window is NOT "ALL") */
function qualitySummaryOf(rows) {
  const v = rows.map(r => r.score).filter(x => x != null);
  const n = rows.length;
  const pct = k => n ? Math.round(rows.filter(r => r.status === k).length / n * 1000) / 10 : 0;
  return {
    avg_score: v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length * 10) / 10 : null,
    excellent_pct: pct("excellent"), critical_pct: pct("critical"), warning_pct: pct("warning"), total_cycles: n,
  };
}
function withRolling(rows) {      // 10-cycle rolling average restarted inside the window
  const scores = rows.map(r => r.score);
  return rows.map((r, i) => {
    const w = scores.slice(Math.max(0, i - 9), i + 1).filter(s => s != null);
    return { ...r, rolling_avg: w.length ? Math.round(w.reduce((a, b) => a + b, 0) / w.length * 10) / 10 : null };
  });
}
function physicsWindow(baseChecks, rows) {
  const checks = {};
  let tp = 0, tt = 0;
  Object.entries(baseChecks).forEach(([k, ch]) => {
    const evald = rows.filter(r => (r.checked || []).includes(k));
    const failRows = evald.filter(r => (r.violations || []).some(v => v.check === k));
    const total = evald.length, fail = failRows.length, pass = total - fail;
    tp += pass; tt += total;
    checks[k] = {
      ...ch, pass, fail, total,
      pass_rate: total ? Math.round(pass / total * 1000) / 10 : null,
      violations: failRows.slice(0, 5).map(r => ({ cycle: r.index, time: r.time, value: r.violations.find(v => v.check === k)?.value })),
    };
  });
  return { checks, overall_pass_rate: tt ? Math.round(tp / tt * 1000) / 10 : null };
}

function ContextBox({items}) {
  if(!items?.length) return null;
  return (
    <div style={{marginTop:12,padding:"10px 14px",background:`${C.panel}cc`,border:`1px solid ${C.border}`,borderRadius:8,display:"flex",flexWrap:"wrap",gap:16}}>
      {items.map((item,i)=>(
        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:6,minWidth:180}}>
          <span style={{color:item.color||C.muted,fontSize:13,flexShrink:0,display:"flex",alignItems:"center"}}>{item.icon||"•"}</span>
          <div>
            <div style={{fontFamily:MONO,fontSize:10,color:item.color||C.muted,fontWeight:700}}>{item.label}</div>
            <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:1,lineHeight:1.4}}>{item.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnimNum({to,dec=1,suffix="",prefix="",duration=900}) {
  const [val,setVal]=useState(null);
  const ref=useRef();
  useEffect(()=>{
    if(to==null||isNaN(Number(to))){setVal(null);return;}
    const end=Number(to);
    const start=performance.now();
    function tick(now){
      const p=Math.min((now-start)/duration,1);
      const e=1-Math.pow(1-p,3);
      setVal(e*end);
      if(p<1) ref.current=requestAnimationFrame(tick);
    }
    ref.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(ref.current);
  },[to,duration]);
  if(val===null) return <span style={{color:C.muted}}>N/A</span>;
  return <span>{prefix}{val.toFixed(dec)}{suffix}</span>;
}

/* Chart body + field-engineer Summary toggle for charts that are not inside ChartCard */
function ChartWithSummary({ height, title, subtitle, accent, trendAware, children }) {
  return (
    <>
      <div style={{ height }}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent} trendAware={trendAware}>{children}</PlotSummary>
    </>
  );
}

function ChartCard({title,subtitle,explainer,children,height=240,accent=C.cyan}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px 14px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:20,right:20,height:2,background:`linear-gradient(to right, transparent, ${accent}88, transparent)`}}/>
      <div style={{marginBottom:12}}>
        <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text}}>{title}</div>
        {subtitle&&<div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:2}}>{subtitle}</div>}
        {explainer&&(
          <div style={{marginTop:8,padding:"7px 10px",background:`${accent}08`,border:`1px solid ${accent}22`,borderRadius:6,fontFamily:MONO,fontSize:10,color:accent,lineHeight:1.5}}>
            {explainer}
          </div>
        )}
      </div>
      <div style={{height}}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent}>{children}</PlotSummary>
    </div>
  );
}

function PanelHeader({ title, subtitle, accent = C.cyan, children }) {
  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: "20px 22px",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 20, right: 20, height: 2,
        background: `linear-gradient(to right, transparent, ${accent}88, transparent)`,
      }}/>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

function LoadingCard({label}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:32,display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{width:16,height:16,border:`2px solid ${C.border2}`,borderTop:`2px solid ${C.cyan}`,borderRadius:"50%",animation:"spin-slow 0.8s linear infinite"}}/>
      <span style={{fontFamily:MONO,fontSize:11,color:C.muted}}>Loading {label}…</span>
    </div>
  );
}

function ErrorCard({label,error}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.red}33`,borderRadius:14,padding:24}}>
      <div style={{fontFamily:MONO,fontSize:11,color:C.red}}>Failed to load {label}</div>
      <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:4}}>{error}</div>
    </div>
  );
}

function useFetch(url) {
  const { apiUrl } = useWell();
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const { selectedWell } = useWell();
  useEffect(()=>{
    const path = url.replace(API, "").replace("http://localhost:5000/api", "");
    const fullUrl = apiUrl(path);
    setLoading(true);setError(null);
    axios.get(fullUrl)
      .then(r=>{setData(r.data);setLoading(false);})
      .catch(e=>{setError(e.message);setLoading(false);});
  },[url, selectedWell]);
  return {data,loading,error};
}

/* CYCLE QUALITY SCORE */
function CycleQualityPanel() {
  const { data, loading, error } = useFetch(`${API}/insights/cycle-quality`);

  const [activeDonutIdx, setActiveDonutIdx] = useState(null);
  const [tooltipPos, setTooltipPos]         = useState({ x: 0, y: 0 });
  const [tooltipEntry, setTooltipEntry]     = useState(null);
  const { advice, loading: aiLoading, fetchAdvice } = useAIAdvice();
  const prevAdviceKeyRef = useRef(null);
  const { isDark } = useTheme();

  const stamped = useMemo(() => (data?.cycles || []).map(c => ({ ...c, _t: parseT(c.time) })), [data]);
  const tw = useTimeWindow(stamped);

  if (loading) return <LoadingCard label="Cycle Quality" />;
  if (error)   return <ErrorCard  label="Cycle Quality" error={error} />;

  const { summary: backendSummary } = data;
  const cycles  = tw.view;   // "ALL" = every cycle exactly as before; otherwise only the cycles inside the window
  const summary = tw.isAll ? backendSummary : qualitySummaryOf(cycles);

  const STATUS_COLOR = {
    excellent: C.green,
    good:      C.teal,
    warning:   C.amber,
    critical:  C.red,
    unknown:   C.muted,
  };

  const chartData = (tw.isAll ? cycles : withRolling(cycles)).slice(-60).map(c => ({
    ...c,
    index: Number(c.index),
    _actualFieldKey: "score",
  }));
  const hasT = hasStamps(chartData);

  const donutData = [
    { name: "Excellent (75–100)", value: cycles.filter(c => c.status === "excellent").length, color: C.green,  statusKey: "excellent" },
    { name: "Good (55–74)",       value: cycles.filter(c => c.status === "good").length,      color: C.teal,   statusKey: "good"      },
    { name: "Warning (35–54)",    value: cycles.filter(c => c.status === "warning").length,   color: C.amber,  statusKey: "warning"   },
    { name: "Critical (0–34)",    value: cycles.filter(c => c.status === "critical").length,  color: C.red,    statusKey: "critical"  },
  ].filter(d => d.value > 0);

  const total = donutData.reduce((s, x) => s + x.value, 0);

  function handleDonutEnter(entry, index) {
    setActiveDonutIdx(index);
    setTooltipEntry(entry);
    const key = `score_${entry.statusKey}_${entry.value}`;
    if (prevAdviceKeyRef.current === key) return;
    prevAdviceKeyRef.current = key;
    const midScore = { excellent: 87, good: 65, warning: 45, critical: 17 };
    const scoreVal = midScore[entry.statusKey] ?? entry.value;
    fetchAdvice("score", scoreVal, "/100", null, {
      status:      entry.statusKey,
      cycleCount:  entry.value,
      totalCycles: cycles.length,
    });
  }

  function handleDonutLeave() {
    setActiveDonutIdx(null);
    setTooltipEntry(null);
  }

  const FloatingDonutTooltip = () => {
    if (!tooltipEntry) return null;
    const d      = tooltipEntry;
    const pct    = total > 0 ? ((d.value / total) * 100).toFixed(1) : "N/A";
    const midScore = { excellent: 87, good: 65, warning: 45, critical: 17 };
    const scoreVal = midScore[d.statusKey] ?? d.value;
    const interp   = FIELD_META.score?.interpret?.(scoreVal);

    return (
      <div style={{
        position:     "absolute",
        left:         tooltipPos.x + 16,
        top:          tooltipPos.y - 20,
        pointerEvents:"none",
        zIndex:       9999,
        background:   "#050d1a",
        border:       `1px solid ${C.border2}`,
        borderRadius: 12,
        padding:      "12px 16px",
        fontFamily:   MONO,
        fontSize:     11,
        boxShadow:    "0 12px 40px #000c",
        maxWidth:     300,
      }}>
        <div style={{
          borderBottom:  `1px solid ${C.border}`,
          paddingBottom: 7,
          marginBottom:  8,
          display:       "flex",
          alignItems:    "center",
          gap:           8,
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: 2,
            background: d.color, flexShrink: 0,
          }} />
          <span style={{ color: C.text, fontWeight: 700, fontSize: 12 }}>{d.name}</span>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 4 }}>
          <span style={{ color: C.muted, fontSize: 10 }}>Cycles</span>
          <span style={{ color: d.color, fontWeight: 700, fontSize: 13 }}>{d.value}</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 8 }}>
          <span style={{ color: C.muted, fontSize: 10 }}>Share of total</span>
          <span style={{ color: C.text, fontSize: 11 }}>{pct}%</span>
        </div>

        {interp && (
          <div style={{
            padding:      "5px 8px",
            background:   `${interp.color}12`,
            border:       `1px solid ${interp.color}33`,
            borderRadius: 6,
            fontSize:     10,
            color:        interp.color,
            lineHeight:   1.5,
            marginBottom: 8,
          }}>
            {interp.text}
          </div>
        )}

        <div style={{
          borderTop:    `1px solid ${C.border}`,
          paddingTop:   8,
          fontSize:     10,
          lineHeight:   1.6,
          background:   aiLoading ? `${C.muted}08` : `${C.cyan}08`,
          borderRadius: 6,
          padding:      "6px 8px",
        }}>
          {aiLoading
            ? <span style={{ color: C.muted }}>Getting AI recommendation...</span>
            : advice
              ? renderAdvice(advice)
              : <span style={{ color: C.muted }}>Hover a slice for AI advice</span>
          }
        </div>
      </div>
    );
  };

  const RefLabel = ({ viewBox, value, color }) => {
    if (!viewBox) return null;
    const { x, y, width } = viewBox;
    const bw = 84, bh = 14;
    const bx = x + width + 4;
    const by = y - bh;
    return (
      <g>
        <rect x={bx} y={by} width={bw} height={bh} rx={3} fill={`${color}25`} />
        <text x={bx + bw/2} y={by + bh - 3} textAnchor="middle" fill={color}
          fontSize={9} fontFamily="'Space Mono', monospace" fontWeight={600}>
          {value}
        </text>
      </g>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {stamped.length > 0 && <DataWindowBar ctl={tw} total={stamped.length} isDark={isDark} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

        {/* Cycle Health Score*/}
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: "20px 18px 28px", position: "relative",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 20, right: 20, height: 2,
            background: `linear-gradient(to right, transparent, ${C.teal}88, transparent)`,
          }} />
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>Cycle Health Score Trend</div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
              Last 60 cycles
              <br />
              0 = Failed, 100 = Perfect
            </div>
            <div style={{
              marginTop: 8, padding: "7px 10px",
              background: `${C.teal}08`, border: `1px solid ${C.teal}22`,
              borderRadius: 6, fontFamily: MONO, fontSize: 10, color: C.teal, lineHeight: 1.5,
            }}>
              Bars below amber color line indicate cycles where the well was underperforming.
              Investigate those cycles for slow velocity or high load factor.
            </div>
          </div>
          <ChartWithSummary height={260} accent={C.teal} trendAware title="Cycle Health Score Trend" subtitle="Health score of the last 60 cycles, 0 = failed, 100 = perfect, with 10-cycle rolling average and quality thresholds">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 90, left: 10, bottom: hasT ? 22 : 36 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="index" tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  tickLine={false} tickFormatter={makeCycleFormatter()} interval={cycleInterval(chartData.length)}
                  height={hasT ? 20 : undefined}
                  label={hasT ? undefined : { value: "Cycle Number →", position: "insideBottom", offset: -20, fill: C.muted, fontSize: 9, fontFamily: MONO }} />
                {dateXAxis(chartData, 5, "Cycle Number →")}
                <YAxis domain={[0, 100]} width={52} tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }}
                  tickLine={false} axisLine={false}
                  label={{ value: "Score /100", angle: -90, position: "insideLeft", fill: C.muted, fontSize: 9, fontFamily: MONO, style: { textAnchor: "middle" } }} />
                <Tooltip content={<SmartTooltip note="Hover to see score breakdown + what caused this rating" />} />
                <ReferenceLine y={75} stroke={`${C.green}55`} strokeDasharray="5 3" label={<RefLabel value="Excellent ≥75" color={C.green} />} />
                <ReferenceLine y={55} stroke={`${C.teal}55`}  strokeDasharray="5 3" label={<RefLabel value="Good ≥55"      color={C.teal}  />} />
                <ReferenceLine y={35} stroke={`${C.amber}55`} strokeDasharray="5 3" label={<RefLabel value="Warning ≥35"   color={C.amber} />} />
                <Bar dataKey="score" name="score" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLOR[entry.status] || C.muted} fillOpacity={0.75} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="rolling_avg" name="rolling_avg"
                  stroke={C.cyan} strokeWidth={2} dot={false} legendType="none" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartWithSummary>
        </div>

        {/* Quality Distribution */}
        <div
          onMouseMove={e => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
          style={{
            background:    C.card,
            border:        `1px solid ${C.border}`,
            borderRadius:  14,
            padding:       "20px 18px",
            position:      "relative",
            overflow:      "visible",
          }}
        >
          <div style={{
            position: "absolute", top: 0, left: 20, right: 20, height: 2,
            background: `linear-gradient(to right, transparent, ${C.purple}88, transparent)`,
          }} />

          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 16 }}>
            Quality Distribution
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 18 }}>
            <div style={{ flexShrink: 0 }}>
              <PieChart width={130} height={130}>
                <Pie
                  data={donutData}
                  cx={65} cy={65}
                  innerRadius={34} outerRadius={58}
                  dataKey="value"
                  paddingAngle={2}
                  onMouseEnter={handleDonutEnter}
                  onMouseLeave={handleDonutLeave}
                  isAnimationActive={true}
                >
                  {donutData.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.color}
                      stroke={activeDonutIdx === i ? "#ffffff" : "none"}
                      strokeWidth={activeDonutIdx === i ? 2 : 0}
                      opacity={activeDonutIdx === null || activeDonutIdx === i ? 1 : 0.45}
                      style={{ cursor: "pointer", transition: "opacity 0.2s" }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </div>

            <div style={{ flex: 1 }}>
              {donutData.map((d, i) => (
                <div
                  key={d.name}
                  onMouseEnter={() => { setActiveDonutIdx(i); handleDonutEnter(d, i); }}
                  onMouseLeave={handleDonutLeave}
                  style={{
                    display:        "flex",
                    justifyContent: "space-between",
                    alignItems:     "center",
                    padding:        "5px 8px",
                    borderBottom:   `1px solid ${C.border}`,
                    borderRadius:   5,
                    cursor:         "pointer",
                    background:     activeDonutIdx === i ? `${d.color}18` : "transparent",
                    transition:     "background 0.18s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: 2, background: d.color,
                      boxShadow:  activeDonutIdx === i ? `0 0 6px ${d.color}` : "none",
                      transition: "box-shadow 0.18s",
                    }} />
                    <span style={{
                      fontFamily: MONO, fontSize: 10,
                      color:      activeDonutIdx === i ? C.text : C.muted,
                      transition: "color 0.18s",
                    }}>
                      {d.name}
                    </span>
                  </div>
                  <span style={{
                    fontFamily: MONO, fontSize: 10,
                    color:      activeDonutIdx === i ? d.color : C.muted,
                    fontWeight: activeDonutIdx === i ? 700 : 400,
                    transition: "all 0.18s",
                  }}>
                    {d.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <FloatingDonutTooltip />

          <div style={{
            background: `${C.teal}0a`, border: `1px solid ${C.teal}22`,
            borderRadius: 10, padding: "12px 16px",
          }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 4 }}>AVERAGE HEALTH SCORE</div>
            <div style={{
              fontFamily: MONO, fontSize: 32, fontWeight: 700,
              color: summary.avg_score >= 75 ? C.green : summary.avg_score >= 55 ? C.teal : C.amber,
            }}>
              <AnimNum to={summary.avg_score} dec={1} suffix="/100" />
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
              {summary.critical_pct}% critical &nbsp;|&nbsp; {summary.excellent_pct}% excellent
            </div>
          </div>

          <PlotSummary inline title="Quality Distribution" subtitle="Share of cycles in each health quality band: excellent, good, warning, critical" accent={C.purple}>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name" name="Cycles per quality band" />
            </PieChart>
          </PlotSummary>
        </div>

        {/* Cycles Score */}
        <div style={{
          gridColumn: "1 / -1", background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 18px",
        }}>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 4 }}>
            Last 10 Cycles, Score Breakdown
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 14 }}>
            Velocity (40pts) = safe 350–1200 ft/min, Load Factor (25pts) = safe &lt;0.5, Plunger Arrival (20pt), Shut-In Efficiency (15pts)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.border2}` }}>
                  {["Cycle","Total Score","Status","Velocity (40pt)","Load Factor (25pt)","Plunger Arrived (20pt)","⏱️ Shut-In (15pt)"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: C.muted, fontWeight: 400, fontSize: 10, letterSpacing: 0.8 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cycles.slice(-10).reverse().map((c, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "8px 12px", color: C.muted }}>C{c.index}</td>
                    <td style={{ padding: "8px 12px", color: STATUS_COLOR[c.status], fontWeight: 700 }}>{c.score ?? "N/A"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{
                        background: `${STATUS_COLOR[c.status]}22`, color: STATUS_COLOR[c.status],
                        padding: "2px 8px", borderRadius: 4, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5,
                      }}>{c.status}</span>
                    </td>
                    {["velocity","load_factor","plunger_arrived","shutin_efficiency"].map(k => (
                      <td key={k} style={{
                        padding: "8px 12px",
                        color: c.breakdown?.[k] != null ? (c.breakdown[k] > 0 ? C.green : C.red) : C.muted,
                      }}>
                        {c.breakdown?.[k] ?? "N/A"} pts
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ContextBox items={[
        {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff3d5a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          ),
          color: C.red,
          label: "Score below 35 = Critical",
          desc: "Cycle had major failure, check if plunger arrived and if velocity was in safe band.",
        },
        {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffb84d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          ),
          color: C.amber,
          label: "Score 35–55 = Warning",
          desc: "Something was suboptimal. Usually load factor too high or velocity borderline.",
        },
        {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00ff88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="12,5.5 13.5,9.5 18,9.8 14.7,12.8 15.8,17.2 12,14.8 8.2,17.2 9.3,12.8 6,9.8 10.5,9.5" strokeWidth="1.4"/>
            </svg>
          ),
          color: C.green,
          label: "Score above 75 = Excellent",
          desc: "All equations passed. Maximize cycles like this by maintaining good shut-in timing.",
        },
      ]} />
    </div>
  );
}

/* PHYSICS VIOLATIONS */
function PhysicsPanel() {
  const {data,loading,error}=useFetch(`${API}/insights/physics-violations`);
  const { isDark } = useTheme();
  const stamped = useMemo(() => (data?.per_cycle || []).map(c => ({ ...c, _t: parseT(c.time) })), [data]);
  const tw = useTimeWindow(stamped);
  if(loading) return <LoadingCard label="Physics Checks"/>;
  if(error)   return <ErrorCard label="Physics Checks" error={error}/>;
  const per_cycle = tw.view;   // "ALL" = every cycle exactly as before
  const timeByIdx = new Map(stamped.map(c => [c.index, c.time]));
  const {checks,overall_pass_rate} = tw.isAll ? {checks:data.checks,overall_pass_rate:data.overall_pass_rate} : physicsWindow(data.checks, per_cycle);
  const CHECK_ICONS={
    load_factor:  {icon:"■",desc:"Eq. Load Factor <0.5: Plunger can lift liquid to surface",unit:"ratio"},
    casing_press: {icon:"◉",desc:"Eq. Casing must be ≥1.5× tubing pressure",unit:"psi"},
    min_shutin:   {icon:"◫",desc:"Eq. Plunger needs minimum time to fall to bottom of well",unit:"min"},
    max_unload:   {icon:"↑",desc:"Eq. Maximum time to unload liquid slug from tubing",unit:"min"},
    velocity_band:{icon:"◈",desc:"Safe velocity: 350–1200 ft/min at surface arrival",unit:"ft/min"},
  };
  const violationSeries=per_cycle.slice(-60).map(c=>({index:Number(c.index),time:c.time,_t:c._t,violations:c.violation_count,_factors:c.factors||[],_actualFieldKey:"violations"}));
  const hasT = hasStamps(violationSeries);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {stamped.length>0 && <DataWindowBar ctl={tw} total={stamped.length} isDark={isDark}/>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
          <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:18}}>Compliance Scorecard</div>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontFamily:MONO,fontSize:10,color:C.muted,letterSpacing:1,textTransform:"uppercase"}}>Overall Pass Rate</div>
            <div style={{fontFamily:MONO,fontSize:48,fontWeight:700,color:overall_pass_rate>=80?C.green:overall_pass_rate>=60?C.amber:C.red,lineHeight:1.1}}>
              <AnimNum to={overall_pass_rate} dec={1} suffix="%"/>
            </div>
            <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:4}}>
              {renderAdvice(overall_pass_rate>=80?"◆ Well operating within limits":overall_pass_rate>=60?"▲ Moderate violations, review setpoints":"■ Frequent violations, intervention needed")}
            </div> 
          </div>
          {Object.entries(checks).map(([key,ch])=>{
            const meta=CHECK_ICONS[key]||{icon:"N/A",desc:""};
            const passColor=ch.pass_rate>=80?C.green:ch.pass_rate>=60?C.amber:C.red;
            return (
              <div key={key} style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <div><span style={{fontSize:14,marginRight:6}}>{meta.icon}</span><span style={{fontFamily:MONO,fontSize:11,color:C.text}}>{ch.name}</span></div>
                  <span style={{fontFamily:MONO,fontSize:11,color:passColor,fontWeight:700}}>{ch.pass_rate}%</span>
                </div>
                <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:4}}>
                  <div style={{height:"100%",width:`${ch.pass_rate}%`,background:`linear-gradient(to right, ${passColor}, ${passColor}aa)`,borderRadius:3,transition:"width 1s ease"}}/>
                </div>
                <div style={{fontFamily:MONO,fontSize:9,color:C.muted}}>{meta.desc}, {ch.fail} failures / {ch.total} cycles</div>
              </div>
            );
          })}
        </div>

        <ChartCard title="Violations Per Cycle" subtitle="Last 60 cycles, 0 = passed all equations, higher = more rules broken" explainer="A violation means the well did something physically inefficient." accent={C.red} height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={violationSeries} margin={{top:5,right:20,left:10,bottom:20}}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} tickFormatter={makeCycleFormatter()} interval={cycleInterval(violationSeries.length)} height={hasT?20:undefined}>
                {!hasT && <Label value="Cycle Number →" position="insideBottom" offset={-12} style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>}
              </XAxis>
              {dateXAxis(violationSeries,5,"Cycle Number →")}
              <YAxis allowDecimals={false} tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false}>
                <Label value="# violations" angle={-90} position="insideLeft" style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>
              </YAxis>
              <Tooltip content={<SmartTooltip note="Number of equations broken this cycle"/>}/>
              <ReferenceLine y={0} stroke={C.green+"44"} label={{value:"0 = Perfect",fill:C.green,fontSize:9,fontFamily:MONO,position:"insideTopRight"}}/>
              <Bar dataKey="violations" name="violations" fillOpacity={0.8} radius={[2,2,0,0]}>
                {violationSeries.map((e,i)=>{
                  const v = Math.round(e.violations ?? 0);
                  return <Cell key={i} fill={v===0?C.teal:v===1?C.amber:C.red} fillOpacity={0.85}/>;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{gridColumn:"1 / -1",background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
          <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>Violation Examples (Top 5 per check)</div>
          <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:14}}>These are specific cycles where equations failed, investigate each to understand the root cause.</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:14}}>
            {Object.entries(checks).filter(([,ch])=>ch.violations?.length>0).map(([key,ch])=>(
              <div key={key} style={{background:`${C.red}08`,border:`1px solid ${C.red}22`,borderRadius:10,padding:"14px 16px"}}>
                <div style={{fontFamily:MONO,fontSize:10,color:C.red,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{CHECK_ICONS[key]?.icon} {ch.name}</div>
                <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginBottom:CHECK_ICONS[key]?.unit?4:10}}>{CHECK_ICONS[key]?.desc}</div>
                {CHECK_ICONS[key]?.unit && <div style={{fontFamily:MONO,fontSize:9,color:C.muted2,marginBottom:10}}>Values in {CHECK_ICONS[key].unit}</div>}
                {ch.violations.map((v,i)=>(
                  <div key={i} style={{fontFamily:MONO,fontSize:10,color:C.muted,padding:"4px 0",borderBottom:`1px solid ${C.border}`}}>
                    C{v.cycle}, <span style={{color:C.amber}}>{JSON.stringify(v.value)}</span>{CHECK_ICONS[key]?.unit && <span style={{color:C.muted}}> {CHECK_ICONS[key].unit}</span>}
                    {(v.time ?? timeByIdx.get(v.cycle)) && <span style={{color:C.muted2}}> · {(v.time ?? timeByIdx.get(v.cycle))}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <ContextBox items={[
        {icon:"■",color:C.red,  label:"Load Factor ≥0.5",      desc:"Most common violation. Means shut-in wasn't long enough, well opened too early. Extend shut-in time."},
        {icon:"◉",color:C.amber,label:"Casing Pressure too low",desc:"Not enough reservoir drive when valve opened. Wait longer before opening, or reduce flowline pressure."},
        {icon:"◈",color:C.teal, label:"Velocity violation",     desc:"Plunger arrived outside 350–1200 ft/min. Too slow = loading, too fast = damage. Tune casing pressure target."},
      ]}/>
    </div>
  );
}

/* EARLY WARNING PANEL, detections from live cycle data; detail + action from AI */
function earlyWarningIcon(w, color) {
  const id = String(w?.id || "");
  const sev = String(w?.severity || "").toLowerCase();
  // Low → down arrow; High → up arrow; Critical conditions → red exclamation in circle
  if (id.includes("low_velocity") || id.includes("low_")) {
    return <span style={{ color, fontSize: 20, fontWeight: 900 }} title="Low">▼</span>;
  }
  if (id.includes("high_velocity") || id.includes("high_")) {
    return <span style={{ color, fontSize: 20, fontWeight: 900 }} title="High">▲</span>;
  }
  if (sev === "critical" || id.includes("critical") || id.includes("missed") || id.includes("failed")) {
    return (
      <span
        title="Critical"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          color,
          fontSize: 13,
          fontWeight: 900,
          lineHeight: 1,
        }}
      >!</span>
    );
  }
  if (sev === "good" || sev === "ok") {
    return <span style={{ color, fontSize: 18 }} title="Good">◆</span>;
  }
  return <span style={{ color, fontSize: 18 }} title="Warning">▲</span>;
}

function EarlyWarningPanel() {
  const { isDark } = useTheme();
  // timestamps of every cycle (all wells), taken from the UNFILTERED response so the presets always have the full picture
  const [times, setTimes] = useState([]);
  const [winQs, setWinQs] = useState("");
  const tw = useTimeWindow(times);

  // window -> query string the backend understands (no params = exactly the old behaviour)
  useEffect(() => {
    const { win, tMax } = tw;
    let qs = "";
    if (win.mode !== "all" && tMax != null) {
      if (win.mode === "cyc30") qs = "?last=30";
      else if (win.mode === "custom") {
        const p = [];
        if (win.from) p.push(`from=${encodeURIComponent(win.from + " 00:00")}`);
        if (win.to)   p.push(`to=${encodeURIComponent(win.to + " 23:59")}`);
        qs = p.length ? "?" + p.join("&") : "";
      } else {
        const pr = PRESETS.find(x => x.id === win.mode);
        qs = `?from=${encodeURIComponent(toQuery(tMax - pr.ms))}&to=${encodeURIComponent(toQuery(tMax))}`;
      }
    }
    setWinQs(qs);
  }, [tw.win, tw.tMax]);   // eslint-disable-line react-hooks/exhaustive-deps

  const {data, loading, error} = useFetch(`${API}/insights/early-warning${winQs}`);

  useEffect(() => {
    if (winQs !== "" || !data?.warnings_by_well) return;
    setTimes(data.warnings_by_well.flatMap(w => (w.cycle_times || []).map(t => ({ well: w.well_name, time: t, _t: parseT(t) }))));
  }, [data, winQs]);

  const bar = times.length > 0
    ? <DataWindowBar ctl={tw} total={times.length} isDark={isDark}
        note="Warnings are re-evaluated per well on the cycles inside the window (cycle counts here are summed over all wells)."/>
    : null;

  if (loading) return <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>{bar}<LoadingCard label="Early Warning" /></div>;
  if (error)   return <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>{bar}<ErrorCard label="Early Warning" error={error} /></div>;

  const warningsByWell = data?.warnings_by_well || [];
  const SEV_CONFIG = {
    critical: { color: C.red,   bg: `${C.red}12`,   border: `${C.red}33`,   label: "CRITICAL" },
    warning:  { color: C.amber, bg: `${C.amber}10`, border: `${C.amber}33`, label: "WARNING" },
    info:     { color: C.green, bg: `${C.green}10`, border: `${C.green}33`, label: "GOOD" },
    good:     { color: C.green, bg: `${C.green}10`, border: `${C.green}33`, label: "GOOD" },
    ok:       { color: C.green, bg: `${C.green}08`, border: `${C.green}22`, label: "GOOD" },
  };

  if (!warningsByWell.length) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {bar}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, fontFamily: MONO, fontSize: 12, color: C.muted }}>
          {winQs ? "No cycles fall inside this window - widen the range." : "No early-warning results returned for the current dataset."}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {bar}
      <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, lineHeight: 1.5 }}>
        Warnings are detected from live cycle statistics (velocity, load factor, arrival flags, production trend). Detail text and operator recommendations are generated by AI for each detected condition.
      </div>
      {warningsByWell.map((well, idx) => (
        <div key={idx} style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          padding: "20px",
          animation: "fade-up 0.4s ease both"
        }}>
          <div style={{
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: 15,
            color: C.cyan,
            marginBottom: 14
          }}>
            {(well.well_name || "").split("/").pop()}, {well.total_cycles} cycles analyzed
            {parseT(well.first_time) != null && parseT(well.last_time) != null && (
              <div style={{ fontFamily: MONO, fontWeight: 400, fontSize: 10, color: C.amber, marginTop: 4 }}>
                {fmtDT(parseT(well.first_time))}  →  {fmtDT(parseT(well.last_time))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {(well.warnings || []).map((w, i) => {
              const cfg = SEV_CONFIG[w.severity] || SEV_CONFIG.info;
              return (
                <div key={w.id || i} style={{
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                  borderRadius: 12,
                  padding: "18px 20px",
                  position: "relative"
                }}>
                  <div style={{
                    position: "absolute",
                    left: 0, top: 0, bottom: 0,
                    width: 4,
                    background: cfg.color,
                    borderRadius: "12px 0 0 12px"
                  }} />

                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <span style={{ width: 28, display: "inline-flex", justifyContent: "center" }}>{earlyWarningIcon(w, cfg.color)}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontFamily: SANS,
                        fontWeight: 700,
                        fontSize: 14,
                        color: cfg.color
                      }}>
                        {w.title}
                      </span>
                      <span style={{
                        marginLeft: 10,
                        fontFamily: MONO,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        color: cfg.color,
                        background: `${cfg.color}18`,
                        border: `1px solid ${cfg.color}44`,
                        borderRadius: 999,
                        padding: "2px 8px",
                      }}>
                        {cfg.label}
                      </span>
                    </div>
                    {w.metric !== undefined && w.metric !== null && (
                      <div style={{
                        textAlign: "right",
                        fontFamily: MONO,
                        fontSize: 13,
                        color: cfg.color,
                        fontWeight: 700
                      }}>
                        {typeof w.metric === "number" ? (Math.abs(w.metric) >= 10 ? w.metric.toFixed(1) : w.metric.toFixed(3)) : w.metric}
                      </div>
                    )}
                  </div>

                  <div style={{
                    fontFamily: MONO,
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: 1.5,
                    marginBottom: 10
                  }}>
                    {w.detail || "Condition detected from recent cycles."}
                  </div>

                  {w.action && (
                    <div style={{
                      padding: "10px 14px",
                      background: `${C.cyan}08`,
                      border: `1px solid ${C.cyan}22`,
                      borderRadius: 8,
                      fontSize: 11,
                      color: C.text,
                      lineHeight: 1.55
                    }}>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: C.cyan, letterSpacing: 0.8, marginBottom: 4, textTransform: "uppercase" }}>
                        AI Recommendation
                      </div>
                      {w.action}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* PRODUCTION FORECAST PANEL */
function ForecastPanel() {
  const [n,setN]=useState(20);
  const {data,loading,error}=useFetch(`${API}/insights/production-forecast?n=${n}`);
  const { isDark } = useTheme();
  const stamped = useMemo(() => (data?.historical || []).map(h => ({ ...h, _t: parseT(h.time) })), [data]);
  const tw = useTimeWindow(stamped);
  if(loading) return <LoadingCard label="Production Forecast"/>;
  if(error)   return <ErrorCard label="Production Forecast" error={error}/>;
  const {historical:allHistorical,forecast,trend_slope,direction,production_column,forecast_summary,daily_rate_mcfd}=data;
  const historical = tw.view;   // the window only trims the ACTUAL history that is drawn; the forecast model itself is unchanged
  const combined=[
    ...historical.map(h=>({index:Number(h.index),time:h.time,_t:h._t,actual:h.actual,_actualFieldKey:"actual"})),
    ...forecast.map(f=>({index:Number(f.index),time:f.time,_t:parseT(f.time),forecast:f.forecast,upper_80:f.upper_80,lower_80:f.lower_80,_actualFieldKey:"forecast"})),
  ];
  const hasT = hasStamps(combined);
  const lastActual=allHistorical[allHistorical.length-1]?.actual;
  const firstForecast=forecast[0]?.forecast;
  const forecastChange=lastActual&&firstForecast?(((firstForecast-lastActual)/lastActual)*100).toFixed(1):null;
  const DIRECTION_COLOR={increasing:C.green,decreasing:C.red,stable:C.teal};
  const trendColor=DIRECTION_COLOR[direction]||C.muted;
  const unit=FIELD_META[production_column]?.unit||"MCF/day";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <PanelHeader
        title={`Production Forecast, ${production_column}`}
        subtitle={`Unit: ${unit}`}
        accent={C.cyan}
      >
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontFamily:MONO,fontSize:10,color:C.muted}}>Forecast ahead:</span>
          {[10,20,30].map(v=>(
            <button key={v} onClick={()=>setN(v)} style={{fontFamily:MONO,fontSize:10,padding:"4px 10px",borderRadius:6,border:`1px solid ${n===v?C.cyan+"66":C.border}`,background:n===v?`${C.cyan}15`:"transparent",color:n===v?C.cyan:C.muted,cursor:"pointer"}}>{v} cycles</button>
          ))}
        </div>
      </PanelHeader>

      {stamped.length>0 && <DataWindowBar ctl={tw} total={stamped.length} isDark={isDark}
        note="Here the window only trims the actual history that is drawn - the forecast is always fitted on the last 30 cycles. Forecast timestamps (est.) = last cycle + median gap between recent cycles."/>}

      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:18}}>
        <ChartCard title="Historical + Forecast with 80% Confidence Band" subtitle="Teal = actual measured, Cyan dashed = AI Model forecast, Shaded = 80% confidence interval" explainer="Where historical (teal) ends and forecast (cyan dashed) begins is the current cycle. The shaded band shows the range where 80% of actual future cycles should fall." accent={C.cyan} height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={combined} margin={{top:10,right:20,left:10,bottom:20}}>
              <defs>
                <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.cyan} stopOpacity={0.15}/>
                  <stop offset="95%" stopColor={C.cyan} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} tickFormatter={makeCycleFormatter()} interval={cycleInterval(combined.length)} height={hasT?20:undefined}>
                {!hasT && <Label value="Cycle Number →" position="insideBottom" offset={-12} style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>}
              </XAxis>
              {dateXAxis(combined,5,"Cycle Number →")}
              <YAxis tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false}>
                <Label value={unit} angle={-90} position="insideLeft" style={{fill:C.muted,fontSize:9,fontFamily:MONO}}/>
              </YAxis>
              <Tooltip content={<SmartTooltip note="Hover to see forecast vs actual and confidence range"/>}/>
              <Legend wrapperStyle={{fontFamily:MONO,fontSize:10,paddingTop:16}}/>
              <Area type="monotone" dataKey="actual" name="actual" stroke={C.teal} fill={`${C.teal}10`} strokeWidth={2} dot={false} connectNulls={false}/>
              <Area type="monotone" dataKey="upper_80" name="Upper 80%" stroke="none" fill="url(#forecastGrad)" dot={false} connectNulls={false} legendType="none"/>
              <Area 
                type="monotone" 
                dataKey="lower_80" 
                name="Lower 80%" 
                stroke="none" 
                fill={C.bg} 
                dot={false} 
                connectNulls={false} 
                legendType="none"
                strokeOpacity={0}
              />
              <Line type="monotone" dataKey="forecast" name="forecast" stroke={C.cyan} strokeWidth={2} dot={false} strokeDasharray="6 3" connectNulls={false}/>
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 16px"}}>
            <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Trend Direction</div>
            <div style={{fontFamily:SANS,fontWeight:800,fontSize:22,color:trendColor,textTransform:"capitalize"}}>
              {direction==="increasing"?"↑":direction==="decreasing"?"↓":"→"} {direction}
            </div>
            <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:4}}>Slope: {trend_slope>=0?"+":""}{trend_slope} {unit}/cycle</div>
            {forecastChange&&<div style={{fontFamily:MONO,fontSize:10,color:trendColor,marginTop:6}}>{forecastChange>=0?"▲":"▼"} {Math.abs(forecastChange)}% projected change</div>}
          </div>
          {daily_rate_mcfd!=null&&(
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 16px",borderTop:`3px solid ${C.cyan}`}}>
              <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Est. Daily Production Rate</div>
              <div style={{fontFamily:MONO,fontSize:28,fontWeight:700,color:C.cyan}}><AnimNum to={daily_rate_mcfd} dec={0}/></div>
              <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:2}}>MCF / day</div>
              <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:4}}>Sum of per-cycle volumes × daily cycle count</div>
            </div>
          )}
          {[
            {label:"Forecast Mean",value:forecast_summary?.mean,unit,    tip:"Expected average over forecast period"},
            {label:"Forecast Min", value:forecast_summary?.min, unit,    tip:"Worst-case scenario lower bound"},
            {label:"Forecast Max", value:forecast_summary?.max, unit,    tip:"Best-case upper bound"},
            {label:"Cycles Ahead", value:forecast_summary?.n_cycles,unit:"cycles",tip:"How far ahead the model predicted"},
          ].map(({label,value,unit:u,tip})=>(
            <div key={label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 16px"}}>
              <div style={{fontFamily:MONO,fontSize:9,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>{label}</div>
              <div style={{fontFamily:MONO,fontSize:18,fontWeight:700,color:C.text}}><AnimNum to={value} dec={1}/> <span style={{fontSize:10,color:C.muted}}>{u}</span></div>
              <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:2}}>{tip}</div>
            </div>
          ))}
        </div>
      </div>

      <ContextBox items={[
        {icon:"◆",color:C.teal, label:"Teal solid = actual history",desc:"Real measured gas production from well, cycle by cycle."},
        {icon:"- -", color:C.cyan, label:"Cyan dashed = AI Model forecast", desc:"Model predicts these values for future cycles based on learned patterns."},
        {icon:"░",color:C.muted,label:"Shaded band = uncertainty",   desc:"80% of future actual values should fall inside this band. Wider = less certainty."},
      ]}/>
    </div>
  );
}

const INSIGHT_TABS = [
  {id:"quality",  label:"Cycle Quality", desc:"Score every cycle 0–100", icon:(
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M6.5 13.5A5.5 5.5 0 0 1 12 7"/><path d="M17.5 13.5A5.5 5.5 0 0 0 12 7"/>
      <line x1="12" y1="12" x2="15" y2="9"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/>
      <polygon points="12,2 13.5,7 18,7 14.3,10 15.7,15 12,12 8.3,15 9.7,10 6,7 10.5,7" strokeWidth="1.2" opacity="0.5"/>
    </svg>
  )},
  {id:"physics",  label:"Physics Check",  desc:"Equations compliance", icon:(
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
      <circle cx="19" cy="19" r="4" fill="none"/><polyline points="17 19 18.5 20.5 21.5 17.5"/>
    </svg>
  )},
  {id:"warning",  label:"Early Warning",  desc:"Trend-based alerts", icon:(
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      <line x1="12" y1="2" x2="12" y2="4"/>
    </svg>
  )},
  {id:"forecast", label:"Forecast", desc:"AI Model predictions", icon:(
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,17 7,11 11,14 15,7"/>
      <polyline points="15,7 19,4 21,3" strokeDasharray="3 2"/>
      <circle cx="21" cy="3" r="1.5" fill="currentColor"/>
      <circle cx="19" cy="4" r="1" fill="currentColor" opacity="0.6"/>
      <line x1="3" y1="20" x2="21" y2="20"/>
    </svg>
  )},
];


function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/>
        <line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/>
      </svg>
    ),
    "■": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/>
        <path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    "◆": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/>
        <path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▽": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▶": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.5}}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

export default function InsightsPage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  const [sub, setSub] = useState(() => {
    const saved = sessionStorage.getItem("insightsTab");
    return saved && saved !== "anomaly" && saved !== "rootcause" ? saved : "quality";
  });

  const handleTabChange = (id) => {
    setSub(id);
    sessionStorage.setItem("insightsTab", id);
  };

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:22,flexWrap:"wrap"}}>
        {INSIGHT_TABS.map(t=>(
          <button key={t.id} onClick={()=>handleTabChange(t.id)} style={{
            fontFamily:MONO,fontSize:11,letterSpacing:0.5,padding:"8px 16px",borderRadius:8,
            border:`1px solid ${sub===t.id?C.purple+"66":C.border}`,
            background:sub===t.id?`${C.purple}18`:"transparent",
            color:sub===t.id?C.purple:C.muted,cursor:"pointer",transition:"all 0.15s",
            display:"flex",flexDirection:"column",alignItems:"center",gap:2,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:6}}><span>{t.icon}</span><span>{t.label}</span></div>
            <span style={{fontSize:9,color:sub===t.id?C.purple:C.muted2}}>{t.desc}</span>
          </button>
        ))}
      </div>
      <div>
        {sub==="quality"   && <CycleQualityPanel/>}
        {sub==="physics"   && <PhysicsPanel/>}
        {sub==="warning"   && <EarlyWarningPanel/>}
        {sub==="forecast"  && <ForecastPanel/>}
      </div>
    </div>
  );
}